import { computed } from 'vue'

/**
 * Composable for grouping items by business week (Friday–Thursday)
 * @param {Ref<Array>} items - Reactive array of items to group
 * @param {Object} options - Configuration options
 * @param {string} options.dateField - Field name to use for grouping (default: 'reviewed_at')
 * @param {string} options.fallbackDateField - Fallback field if primary is null (default: 'start_date')
 * @param {boolean} options.sortByTop20 - Sort Top 20 accounts first within each week (default: false)
 * @param {Function} options.isFullyAssignedFn - Function to check if item is fully assigned (sourced)
 * @param {Function} options.sortFn - Custom sort function for items within each week group
 */
export function useWeekGrouping(items, options = {}) {
  const {
    dateField = 'reviewed_at',
    fallbackDateField = 'start_date',
    sortByTop20 = false,
    isFullyAssignedFn = null,
    sortFn = null
  } = options

  /**
   * Get ISO week number from a date
   * @param {Date} date
   * @returns {number}
   */
  const getISOWeekNumber = date => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
    const dayNum = d.getUTCDay() || 7
    d.setUTCDate(d.getUTCDate() + 4 - dayNum)
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
    return Math.ceil(((d - yearStart) / 86400000 + 1) / 7)
  }

  /**
   * Get business week key in format YYYY-Www
   * Business weeks run Friday–Thursday: items on Fri/Sat/Sun roll into the next ISO week.
   * @param {Date|string} date
   * @returns {string}
   */
  const getISOWeekKey = date => {
    const d = new Date(date)
    // Shift +3 days so Fri/Sat/Sun map to next ISO week, Mon-Thu stay in current
    d.setDate(d.getDate() + 3)
    const week = getISOWeekNumber(d)
    const year = d.getFullYear()
    return `${year}-W${week.toString().padStart(2, '0')}`
  }

  /**
   * Get the Friday (start) of the business week for a given date
   * @param {Date|string} date
   * @returns {Date}
   */
  const getWeekStart = date => {
    const d = new Date(date)
    const day = d.getDay()
    // Go back to the most recent Friday: (day + 2) % 7 gives the offset
    const diff = (day + 2) % 7
    return new Date(d.setDate(d.getDate() - diff))
  }

  /**
   * Format a week label for display (Friday–Thursday range)
   * @param {Date|string} date
   * @returns {string}
   */
  const formatWeekLabel = date => {
    const weekStart = getWeekStart(new Date(date)) // Friday
    const weekEnd = new Date(weekStart)
    weekEnd.setDate(weekEnd.getDate() + 6) // Thursday

    // Week number: Friday + 3 = Monday of the ISO week
    const monday = new Date(weekStart)
    monday.setDate(monday.getDate() + 3)
    const weekNum = getISOWeekNumber(monday)
    const year = monday.getFullYear()

    const formatDay = d => d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })

    return `Week ${weekNum} - ${formatDay(weekStart)} t/m ${formatDay(weekEnd)} ${year}`
  }

  /**
   * Group items by week, sorted by week (most recent first)
   */
  const groupedByWeek = computed(() => {
    if (!items.value || items.value.length === 0) return []

    const groups = {}

    for (const item of items.value) {
      const dateValue = item[dateField] || item[fallbackDateField]
      const itemDate = dateValue ? new Date(dateValue) : new Date()
      const weekKey = getISOWeekKey(itemDate)

      if (!groups[weekKey]) {
        groups[weekKey] = {
          weekKey,
          weekLabel: formatWeekLabel(itemDate),
          weekStart: getWeekStart(itemDate),
          items: []
        }
      }
      groups[weekKey].items.push(item)
    }

    // Sort by week (most recent first)
    const sortedGroups = Object.values(groups).sort(
      (a, b) => new Date(b.weekStart) - new Date(a.weekStart)
    )

    // Sort items within each group
    if (sortFn) {
      // Custom sort function takes precedence
      for (const group of sortedGroups) {
        group.items = [...group.items].sort(sortFn)
      }
    } else if (isFullyAssignedFn || sortByTop20) {
      for (const group of sortedGroups) {
        group.items = [...group.items].sort((a, b) => {
          // 1. Unsourced items always first (if isFullyAssignedFn is provided)
          if (isFullyAssignedFn) {
            const aFullyAssigned = isFullyAssignedFn(a)
            const bFullyAssigned = isFullyAssignedFn(b)
            if (aFullyAssigned !== bFullyAssigned) {
              return aFullyAssigned ? 1 : -1 // Unsourced first
            }
          }

          // 2. Top 20 items first (if sortByTop20 is enabled)
          if (sortByTop20 && a.is_top20 !== b.is_top20) {
            return a.is_top20 ? -1 : 1
          }

          // 3. Within same category, sort by expected_revenue descending
          return (b.expected_revenue || 0) - (a.expected_revenue || 0)
        })
      }
    }

    return sortedGroups
  })

  /**
   * Get available week options for filtering
   */
  const weekOptions = computed(() => {
    return groupedByWeek.value.map(g => ({
      value: g.weekKey,
      label: g.weekLabel
    }))
  })

  return {
    // Computed
    groupedByWeek,
    weekOptions,

    // Helpers (exposed for direct use and testing)
    getISOWeekNumber,
    getISOWeekKey,
    getWeekStart,
    formatWeekLabel
  }
}
